import * as React from "react";
import { t } from "i18next";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Form, notification } from "antd";
import { observer } from "mobx-react-lite";
import { onError } from "src/common/onError";
import { EdCardValues } from "src/pages/EntityCardPage/apiEntityCard";
import { EntityCardStore } from "src/pages/EntityCardPage/EntityCardStore";
import { ZUid } from "src/types/zUid";
import { TShapeForm } from "src/components/TShapeForm/TShapeForm";
import { confirmDelete } from "src/components/ModalDelete";
import { PageUrl } from "src/routes/PageUrl";
import { makeUrl } from "src/routes/makeUrl";
import { EntityConfirmHeader } from "./EntityConfirmHeader";
import iconBlankLink from "./blankLink.svg";

const entityCardFormName = "EntitySubForm";
const idEntityFormContainer = "EntitySubFormContainer";

export interface PropsEntityEditor {
  store: EntityCardStore;
  onSuccess(entityId?: ZUid): void;
  onDelete?(): void;
  deleting?: boolean;
  submitText: string;
}

export const msgMultiInstancesCreated = (count: number) =>
  t("New instances were created", { count });

export const EntityEditor: React.FC<PropsEntityEditor> = observer((props) => {
  const { store, onSuccess, submitText, onDelete, deleting } = props;
  const { rootBlock, formStore, entityName, curEntityId } = store;
  const [form] = Form.useForm();
  if (!rootBlock) return null;
  const onSubmit = async (values: EdCardValues) => {
    try {
      if (store.entityId) {
        return store.save(values);
      }
      return store.create(values);
    } catch (error) {
      onError(error);
    }
    return undefined;
  };
  const onSuccessInternal = (values: EdCardValues | EdCardValues[]) => {
    if (!store.entityId) {
      const message = Array.isArray(values)
        ? msgMultiInstancesCreated(values.length)
        : t("New instance has been created");
      notification.success({
        message,
        duration: 1,
      });
    }
    if (!Array.isArray(values) && typeof values.entityId === "string") {
      onSuccess(values.entityId);
    } else {
      onSuccess();
    }
  };
  const deleteHandler = () => {
    confirmDelete(t("Do you want to delete the current entry?"), () =>
      onDelete?.(),
    );
  };
  const extraButtons =
    store.canDelete && !!onDelete ? (
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={deleteHandler}
        loading={deleting}
      >
        {t("Delete")}
      </Button>
    ) : undefined;
  const extraRightButtons = curEntityId ? (
    <Button
      icon={<img src={iconBlankLink} alt="" />}
      href={makeUrl(PageUrl.entityCard, { id: curEntityId })}
      target="_blank"
      title="Открыть в новой вкладке"
      style={{ marginRight: 12 }}
    />
  ) : undefined;
  return (
    <TShapeForm
      title={entityName}
      form={form}
      formProps={{
        name: entityCardFormName,
        initialData: store.initialData,
        block: rootBlock,
        store: formStore,
        submit: onSubmit,
        submitText,
        onSuccess: onSuccessInternal,
        extraButtons,
        onValuesChange(changedValues, values) {
          store.onFormValuesChange(form, changedValues, values);
        },
        disabled: store.buzy,
      }}
      extraRightButtons={extraRightButtons}
      rightPartProps={{ id: idEntityFormContainer }}
      header={
        store.onConfirm ? <EntityConfirmHeader store={store} /> : undefined
      }
    />
  );
});
